import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, timer, Subject } from 'rxjs';
import { switchMap, tap, takeUntil, shareReplay } from 'rxjs/operators';
import { environment } from '../../environments/environment';

// ==========================================
// INTERFACES
// ==========================================

export interface CheckoutAlert {
    reservaId: number;
    codigoReserva: string;
    habitacionNumero: string;
    nombreHuesped: string;
    fechaCheckOut: string;
    horasRestantes: number;
}

export interface CleaningAlert {
    habitacionId: number;
    numeroHabitacion: string;
    piso: number;
    tiempoEspera: number | null;
}

export interface LowStockAlert {
    productoId: number;
    nombre: string;
    stockActual: number;
    umbralMinimo: number;
    diferencia: number;
}

export interface CheckoutAlertsResponse {
    alertas: CheckoutAlert[];
}

export interface CleaningAlertsResponse {
    totalPendientes: number;
    habitaciones: CleaningAlert[];
}

export interface LowStockAlertsResponse {
    totalProductos: number;
    productos: LowStockAlert[];
}

// ==========================================
// ALERTS SERVICE
// ==========================================

@Injectable({
    providedIn: 'root'
})
export class AlertsService {
    private readonly http = inject(HttpClient);
    private readonly API_BASE_URL = environment.apiBaseUrl;

    // Polling control
    private stopPolling$ = new Subject<void>();
    private pollingInterval = 5 * 60 * 1000; // 5 minutes in milliseconds

    // Signals for reactive state
    checkoutAlerts = signal<CheckoutAlert[]>([]);
    cleaningAlerts = signal<CleaningAlert[]>([]);
    lowStockAlerts = signal<LowStockAlert[]>([]);

    totalCheckoutCount = signal<number>(0);
    totalCleaningCount = signal<number>(0);
    totalLowStockCount = signal<number>(0);

    // Total combined alerts count
    get totalAlertsCount(): number {
        return this.totalCheckoutCount() + this.totalCleaningCount() + this.totalLowStockCount();
    }

    /**
     * Get checkout pending alerts
     */
    getCheckoutPending(): Observable<CheckoutAlertsResponse> {
        const url = `${this.API_BASE_URL}/admin/alerts/checkout-pending`;
        return this.http.get<CheckoutAlertsResponse>(url).pipe(
            tap(response => {
                this.checkoutAlerts.set(response.alertas);
                this.totalCheckoutCount.set(response.alertas.length);
            })
        );
    }

    /**
     * Get cleaning pending alerts
     */
    getCleaningPending(): Observable<CleaningAlertsResponse> {
        const url = `${this.API_BASE_URL}/admin/alerts/cleaning-pending`;
        return this.http.get<CleaningAlertsResponse>(url).pipe(
            tap(response => {
                this.cleaningAlerts.set(response.habitaciones);
                this.totalCleaningCount.set(response.totalPendientes);
            })
        );
    }

    /**
     * Get low stock alerts
     */
    getLowStock(): Observable<LowStockAlertsResponse> {
        const url = `${this.API_BASE_URL}/admin/alerts/low-stock`;
        return this.http.get<LowStockAlertsResponse>(url).pipe(
            tap(response => {
                this.lowStockAlerts.set(response.productos);
                this.totalLowStockCount.set(response.totalProductos);
            })
        );
    }

    /**
     * Fetch all alerts at once
     */
    getAllAlerts(): void {
        this.getCheckoutPending().subscribe();
        this.getCleaningPending().subscribe();
        this.getLowStock().subscribe();
    }

    /**
     * Start automatic polling for alerts
     * @param intervalMs - Polling interval in milliseconds (default: 5 minutes)
     */
    startPolling(intervalMs?: number): void {
        if (intervalMs) {
            this.pollingInterval = intervalMs;
        }

        // Fetch immediately
        this.getAllAlerts();

        // Then poll at intervals
        timer(this.pollingInterval, this.pollingInterval)
            .pipe(
                takeUntil(this.stopPolling$),
                switchMap(() => {
                    this.getAllAlerts();
                    return [];
                })
            )
            .subscribe();
    }

    /**
     * Stop automatic polling
     */
    stopPolling(): void {
        this.stopPolling$.next();
    }

    /**
     * Clear all alerts data
     */
    clearAlerts(): void {
        this.checkoutAlerts.set([]);
        this.cleaningAlerts.set([]);
        this.lowStockAlerts.set([]);
        this.totalCheckoutCount.set(0);
        this.totalCleaningCount.set(0);
        this.totalLowStockCount.set(0);
    }
}
